import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const UserProfileModal = ({ userId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, first_name, last_name, email, location, avatar_url, experience_level, created_at, bio, phone, instagram_handle, website_url, membership_tier, role, is_banned, has_equipment, equipment_details, birth_date')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      const firstLast = [data?.first_name, data?.last_name]?.filter(Boolean)?.join(' ')?.trim();
      const fullName = data?.full_name?.trim() || firstLast || 'İsimsiz Kullanıcı';

      setProfile({
        ...data,
        displayName: fullName,
        memberSince: data?.created_at
          ? new Date(data?.created_at)?.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
          : 'Bilinmiyor',
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name?.split(' ');
    if (parts?.length >= 2) return `${parts?.[0]?.[0] || ''}${parts?.[1]?.[0] || ''}`?.toUpperCase();
    return name?.[0]?.toUpperCase() || 'U';
  };

  const getExperienceLevelLabel = (level) => {
    const levels = { beginner: 'Başlangıç', intermediate: 'Orta', advanced: 'İleri', expert: 'Uzman' };
    return levels?.[level] || 'Belirtilmemiş';
  };

  const getExperienceLevelColor = (level) => {
    const colors = {
      beginner: 'bg-blue-100 text-blue-700',
      intermediate: 'bg-green-100 text-green-700',
      advanced: 'bg-orange-100 text-orange-700',
      expert: 'bg-purple-100 text-purple-700',
    };
    return colors?.[level] || 'bg-gray-100 text-gray-700';
  };

  const getMembershipLabel = (tier) => {
    const tiers = { free: 'Ücretsiz', premium: 'Premium', pro: 'Pro' };
    return tiers?.[tier] || tier || 'Ücretsiz';
  };

  const getMembershipColor = (tier) => {
    const colors = { free: 'bg-gray-100 text-gray-600', premium: 'bg-yellow-100 text-yellow-700', pro: 'bg-purple-100 text-purple-700' };
    return colors?.[tier] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Kullanıcı Profili</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : !profile ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="UserX" size={48} className="mx-auto mb-3" />
            <p>Profil bulunamadı.</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Avatar + Name */}
            <div className="flex flex-col items-center mb-6">
              {profile?.avatar_url ? (
                <Image
                  src={profile?.avatar_url}
                  alt={profile?.displayName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary/20 mb-3"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center border-4 border-primary/20 mb-3">
                  <span className="text-3xl font-bold text-primary">{getInitials(profile?.displayName)}</span>
                </div>
              )}
              <h3 className="text-xl font-bold text-foreground">{profile?.displayName}</h3>
              {profile?.email && (
                <p className="text-sm text-muted-foreground mt-1">{profile?.email}</p>
              )}

              {/* Badges row */}
              <div className="flex flex-wrap gap-2 mt-3 justify-center">
                {profile?.experience_level && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getExperienceLevelColor(profile?.experience_level)}`}>
                    {getExperienceLevelLabel(profile?.experience_level)}
                  </span>
                )}
                {profile?.membership_tier && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getMembershipColor(profile?.membership_tier)}`}>
                    {getMembershipLabel(profile?.membership_tier)}
                  </span>
                )}
                {profile?.role === 'admin' && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Admin</span>
                )}
                {profile?.is_banned && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Yasaklı</span>
                )}
              </div>
            </div>

            {/* Info Grid */}
            <div className="space-y-3">
              {profile?.location && (
                <InfoRow icon="MapPin" label="Konum" value={profile?.location} />
              )}
              {profile?.phone && (
                <InfoRow icon="Phone" label="Telefon" value={profile?.phone} />
              )}
              {profile?.birth_date && (
                <InfoRow
                  icon="Calendar"
                  label="Doğum Tarihi"
                  value={new Date(profile?.birth_date)?.toLocaleDateString('tr-TR')}
                />
              )}
              <InfoRow icon="Clock" label="Üyelik Tarihi" value={profile?.memberSince} />
              {profile?.has_equipment && (
                <InfoRow icon="Package" label="Ekipman" value={profile?.has_equipment === 'yes' ? 'Var' : 'Yok'} />
              )}
              {profile?.equipment_details && (
                <InfoRow icon="Info" label="Ekipman Detayı" value={profile?.equipment_details} />
              )}
              {profile?.instagram_handle && (
                <InfoRow icon="Instagram" label="Instagram" value={`@${profile?.instagram_handle}`} />
              )}
              {profile?.website_url && (
                <InfoRow icon="Globe" label="Web Sitesi" value={profile?.website_url} />
              )}
            </div>

            {/* Bio */}
            {profile?.bio && (
              <div className="mt-4 p-4 bg-muted/50 rounded-xl">
                <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">Hakkında</p>
                <p className="text-sm text-foreground leading-relaxed">{profile?.bio}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 flex-shrink-0 mt-0.5">
      <Icon name={icon} size={15} className="text-primary" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground truncate">{value}</p>
    </div>
  </div>
);

export default UserProfileModal;
